<script setup>
import { ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import AppLayout from '@/Layouts/AppLayout.vue'
import PrimaryButton from '@/Components/PrimaryButton.vue'
import SecondaryButton from '@/Components/SecondaryButton.vue'
import TextInput from '@/Components/TextInput.vue'
import PaginationLinks from '@/Components/PaginationLinks.vue'
import DialogModal from '@/Components/DialogModal.vue'

const props = defineProps({
  carriers: Object,
  pageSearchQuery: String,
})

const searchQuery = ref(props.pageSearchQuery || '')
const deletingItem = ref(null)

const openDeleteModal = (item) => deletingItem.value = item
const closeDeleteModal = () => deletingItem.value = null

const makeSearchRequest = _.debounce(() => {
  Inertia.get(route('admin.carrier.index'), { query: searchQuery.value }, { preserveScroll: true })
}, 300)

const deleteItem = (id) => {
  Inertia.delete(route('admin.carrier.destroy', id), { preserveScroll: true })
  deletingItem.value = null
}
</script>

<template>
  <AppLayout title="Carriers">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">Carriers</h2>
    </template>

    <div class="mx-auto py-10 sm:px-6 lg:px-8">
      <div class="mb-4 flex justify-between items-center">
        <TextInput
          v-model="searchQuery"
          placeholder="Search carriers by name or link..."
          @input="makeSearchRequest"
          class="w-1/2"
        />
        <a :href="route('admin.carrier.create')">
          <PrimaryButton>Add Carrier</PrimaryButton>
        </a>
      </div>

      <table class="min-w-full w-full text-left text-sm">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-2">Name</th>
            <th class="px-4 py-2">Weblink</th>
            <th class="px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in carriers.data" :key="c.carrier_id" class="border-b">
            <td class="px-4 py-2">{{ c.carrier_name }}</td>
            <td class="px-4 py-2">
              <a
                v-if="c.weblink"
                :href="c.weblink"
                target="_blank"
                rel="noopener noreferrer"
                class="text-blue-600 underline"
              >
                {{ c.weblink }}
              </a>
              <span v-else class="text-gray-400">—</span>
            </td>
            <td class="px-4 py-2 space-x-3">
              <a :href="route('admin.carrier.show', c.carrier_id)" class="fa fa-eye" title="View" />
              <a :href="route('admin.carrier.edit', c.carrier_id)" class="fa fa-pen" title="Edit" />
              <button
                @click="openDeleteModal(c)"
                class="fa fa-trash hover:text-red-800"
                title="Disable"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="mt-4">
        <PaginationLinks :pagination="carriers" />
      </div>
    </div>

    <DialogModal :show="deletingItem !== null" @close="closeDeleteModal">
      <template #title><b>Please confirm</b></template>
      <template #content>
        You are about to delete carrier
        <b v-text="deletingItem?.carrier_name"></b>.
        Are you sure?
      </template>
      <template #footer>
        <SecondaryButton @click="closeDeleteModal" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="deleteItem(deletingItem.carrier_id)">
          Confirm
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>